import React from 'react';
import { Typography, Row, Col, Space, Descriptions, Table, Card, Tabs, Tag, Divider, Tooltip, Alert } from 'antd';
import type { TableProps } from 'antd';
import { NodeIndexOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { BaseChange, ImpactedItem } from '../../../types/changeAwareness';
import { CodeDisplay, SeverityTag, ChangeTypeTag } from './UtilityComponents';
import RelatedItemsPanel from '../../common/RelatedItemsPanel';

const { Text, Title, Paragraph } = Typography;
const { TabPane } = Tabs;

// Extended change interface with all properties needed by expanded rows
export interface StandardExpandedChange extends Omit<BaseChange, 'description'> {
  id: string;
  title: string;
  description?: string;
  severity: string;
  status: string;
  date: string;
  author: string;
  category: string; 
  impactedItems?: ImpactedItem[];
  changeType: string;
  technicalDetails?: any;
}

// Common expanded row component for change items
export interface ExpandedRowProps<T extends StandardExpandedChange> {
  record: T;
  showTechnicalDetails?: boolean;
  showImpact?: boolean;
  showDependencies?: boolean;
  showDocuments?: boolean;
  showCustomSection?: boolean;
  customSectionTitle?: string;
  customSectionContent?: React.ReactNode;
}

export const StandardExpandedRow = <T extends StandardExpandedChange>({
  record,
  showTechnicalDetails = true,
  showImpact = true,
  showDependencies = false
}: {
  record: T;
  showTechnicalDetails?: boolean;
  showImpact?: boolean;
  showDependencies?: boolean;
  showDocuments?: boolean;
  showCustomSection?: boolean;
  customSectionTitle?: string;
  customSectionContent?: React.ReactNode;
}) => {
  const { Title, Text, Paragraph } = Typography;

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Function to render a section header with optional tooltip
  const renderSectionHeader = (title: string, tooltip?: string) => (
    <Divider orientation="left">
      <Space>
        <Text strong>{title}</Text>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
          </Tooltip>
        )}
      </Space>
    </Divider>
  );

  // Define table columns for impacted items
  const impactColumns: TableProps<any>['columns'] = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (text) => <Tag color="blue">{text.charAt(0).toUpperCase() + text.slice(1)}</Tag>,
    },
    {
      title: 'Item',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <CodeDisplay>{text}</CodeDisplay>,
    }
  ];

  // Parse and organize impact/dependency information
  // Use impactedItems directly if available in the new format
  const impactedItemsData = record.impactedItems || [];

  // Prepare technical details for display
  const technicalDetailsEntries = record.technicalDetails
    ? Object.entries(record.technicalDetails).map(([key, value]) => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1'),
        value: typeof value === 'object' ? JSON.stringify(value) : String(value),
      }))
    : [];

  // Map for operational scenario items
  const operationalScenarioItems = impactedItemsData
    .filter(item => item.type.toLowerCase() === 'operationalscenario')
    .map(item => ({
      title: item.name,
      status: 'Current',
      date: new Date().toISOString(),
      author: 'System',
      ...item
    }));

  // Transform impacted items into the RelatedItemsPanel format
  const mapImpactedItemsToRelatedItems = () => {
    // Group items by type
    const groupedItems = impactedItemsData.reduce((acc, item) => {
      const type = item.type.toLowerCase();
      if (!acc[type]) {
        acc[type] = [];
      }
      
      // Convert to the RelatedItemsPanel item format
      acc[type].push({
        title: item.name,
        status: 'Current',
        date: new Date().toISOString(), // Use current date as fallback
        author: 'System',
        ...item  // Spread other properties (including id)
      });
      
      return acc;
    }, {} as Record<string, any[]>);

    return {
      mission: groupedItems.mission || [],
      requirements: groupedItems.requirement || [],
      functions: groupedItems.function || [],
      logical: groupedItems.logical || [],
      cad: groupedItems.cad || [],
      ebom: groupedItems.bom || [],
      models: groupedItems.model || [],
      automation: groupedItems.automation || [],
      parameters: groupedItems.parameter || [],
      operationalScenario: operationalScenarioItems, // Add operational scenario items
    };
  };

  // Prepare related items data for the RelatedItemsPanel
  const relatedItems = mapImpactedItemsToRelatedItems();
  
  // Check if there are any related items to display
  const hasRelatedItems = Object.values(relatedItems).some(items => items.length > 0);
  
  // Create a current item object from the record
  const currentItemType = record.category.toLowerCase();
  console.log("Original category from record:", record.category);
  
  // Simplified function to map the current item's type to a standardized category
  const getCategoryType = () => {
    // Extract ID prefix to determine type (most reliable method)
    const idPrefix = record.id.split('-')[0]?.toLowerCase();
    console.log("ID prefix for type determination:", idPrefix);
    
    // First check ID prefix - this is the most reliable indicator
    if (idPrefix === 'req') return 'requirements';
    if (idPrefix === 'param') return 'parameters';
    if (idPrefix === 'func') return 'functions';
    if (idPrefix === 'mission') return 'mission';
    if (idPrefix === 'scen') return 'operationalScenario';
    if (idPrefix === 'log') return 'logical';
    if (idPrefix === 'cad') return 'cad';
    if (idPrefix === 'bom') return 'ebom';
    
    // If ID prefix didn't match, try the record category
    const category = record.category.toLowerCase().trim();
    console.log("Using category from record:", category);
    
    if (category.includes('requirement')) return 'requirements';
    if (category.includes('parameter')) return 'parameters';
    if (category.includes('function')) return 'functions';
    if (category.includes('mission')) return 'mission';
    if (category.includes('scenario')) return 'operationalScenario';
    if (category.includes('logical')) return 'logical';
    if (category.includes('cad')) return 'cad';
    if (category.includes('bom') || category.includes('engineering')) return 'ebom';
    
    // Default fallback - use the raw category
    return category;
  };
  
  // Get the exact category that should be highlighted
  const highlightCategory = getCategoryType();
  console.log("Category that should be highlighted:", highlightCategory);
  
  // Create the current item in the format expected by RelatedItemsPanel
  const currentItem = {
    id: record.id,
    title: record.title,
    description: record.description,
    status: record.status as 'Current' | 'Active' | 'Modified' | 'Released' | 'In Development' | 'Deprecated' | 'Archived' | 'Updated' | 'Completed',
    date: record.date,
    author: record.author
  };

  // Handle creating a relationship
  const handleCreateRelationship = (categoryKey: string) => {
    console.log(`Creating relationship with ${categoryKey} for item ${record.id}`);
    // In a real implementation, this would open a modal or navigate to create a relationship
    alert(`Creating relationship with ${categoryKey} for item ${record.id}`);
  };

  return (
    <div className="expanded-row" style={{ padding: '0 20px 20px 20px' }}>
      <Row gutter={[24, 16]}>
        {/* Impact Analysis Section - Full Width */}
        {showImpact && (
          <Col xs={24}>
            <RelatedItemsPanel
              mission={relatedItems.mission}
              requirements={relatedItems.requirements}
              functions={relatedItems.functions}
              logical={relatedItems.logical}
              cad={relatedItems.cad}
              ebom={relatedItems.ebom}
              models={relatedItems.models}
              automation={relatedItems.automation}
              parameters={relatedItems.parameters}
              defaultActiveTab={Object.keys(relatedItems).find(key => relatedItems[key as keyof typeof relatedItems].length > 0)}
              currentItem={currentItem}
              currentItemType={highlightCategory}
              onCreateRelationship={handleCreateRelationship}
            />
          </Col>
        )}
      </Row>
    </div>
  );
};

// We're keeping these interfaces and functions for backward compatibility,
// but they won't be used in the UI anymore

// Technical Details Section
interface TechnicalDetailsSectionProps<T extends StandardExpandedChange> {
  record: T;
}

function TechnicalDetailsSection<T extends StandardExpandedChange>({ record }: TechnicalDetailsSectionProps<T>) {
  return null;
}

// Dependencies Section
interface DependenciesSectionProps<T extends StandardExpandedChange> {
  record: T;
}

function DependenciesSection<T extends StandardExpandedChange>({ record }: DependenciesSectionProps<T>) {
  return null;
}

// Documents Section
interface DocumentsSectionProps<T extends StandardExpandedChange> {
  record: T;
}

function DocumentsSection<T extends StandardExpandedChange>({ record }: DocumentsSectionProps<T>) {
  return null;
} 